import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connectSearchBox } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as CloseIcon } from '../../../assets/images/svg/close.svg';
import { ReactComponent as SearchIcon } from '../../../assets/images/svg/magnifying-glass.svg';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const IconButton = styled.button`
  border: none;
  background: transparent;
  padding: ${pxToRem(6)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
  }
`;

const ExpandedRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  flex: 1;
`;

const InputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(6)};
  border: 0 solid ${themeColors.colorBorderPrimary};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(6)} ${pxToRem(16)};
  background: #f5f5f5;
  flex: 1;
`;

const Input = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  flex: 1;
  min-width: 0;

  &::placeholder {
    color: ${themeColors.colorLabel};
  }
`;

const ClearButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0;
  color: ${themeColors.colorTextPrimary};

  > svg {
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
  }
`;

const SellFromHomeSearchInput = ({
  currentRefinement,
  refine,
  isExpanded,
  onToggleExpanded,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  if (!isExpanded) {
    return (
      <IconButton
        type="button"
        aria-label={t('sellWizard.sfhWizard.searchAriaLabel')}
        onClick={onToggleExpanded}
      >
        <SearchIcon />
      </IconButton>
    );
  }

  return (
    <ExpandedRow>
      <InputWrap>
        <Input
          ref={inputRef}
          type="search"
          value={currentRefinement}
          onChange={(event) => refine(event.currentTarget.value)}
          placeholder={t('sellWizard.sfhWizard.searchPlaceholder')}
        />
        <ClearButton
          type="button"
          aria-label={t('sellWizard.sfhWizard.searchAriaLabel')}
          onClick={() => {
            refine('');
            onToggleExpanded();
          }}
        >
          <CloseIcon />
        </ClearButton>
      </InputWrap>
      <IconButton
        type="button"
        aria-label={t('sellWizard.sfhWizard.searchAriaLabel')}
        onClick={() => {
          refine('');
          onToggleExpanded();
        }}
      >
        <SearchIcon />
      </IconButton>
    </ExpandedRow>
  );
};

SellFromHomeSearchInput.propTypes = {
  currentRefinement: PropTypes.string.isRequired,
  refine: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
};

export default connectSearchBox(SellFromHomeSearchInput);
